import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, message, Typography, Space } from 'antd';

const { Paragraph, Text } = Typography;

interface OtpModalProps {
  visible: boolean;
  onCancel: () => void;
  onVerify: (otp: string) => Promise<void>;
  modalTitle?: string;
  closable?: boolean;
  handleOtpResend?: () => void;
  resend?: boolean;
}

const OTPModal: React.FC<OtpModalProps> = ({
  visible,
  onCancel,
  onVerify,
  modalTitle = 'OTP Verification',
  closable = true,
  handleOtpResend,
  resend = true,
}) => {
  const [otp, setOtp] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(30);
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (visible && isResendDisabled) {
      setCountdown(30);
      setIsResendDisabled(true);
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
        if (countdown === 0) {
          setIsResendDisabled(false);
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [visible, setCountdown, setIsResendDisabled, isResendDisabled]);

  const handleVerify = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onVerify(otp);
      setOtp('');
      // message.success('OTP verified successfully!');
    } catch (error) {
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (handleOtpResend) {
      handleOtpResend();
      setCountdown(30);
      setIsResendDisabled(true);
    }
  };

  return (
    <Modal
      title={modalTitle}
      open={visible}
      closable={closable}
      onCancel={onCancel}
      maskClosable={closable}
      keyboard={closable}
      footer={[
        ...(closable
          ? [
              <Button key="cancel" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>,
            ]
          : []),
        <Button
          key="verify"
          type="primary"
          onClick={handleVerify}
          loading={loading}
          disabled={loading || otp.length !== 6}
        >
          Verify
        </Button>,
        ...(resend
          ? [
              <Button
                key="resend"
                onClick={handleResend}
                disabled={isResendDisabled}
              >
                Resend OTP {isResendDisabled && `(${countdown}s)`}
              </Button>,
            ]
          : []),
      ]}
      centered
    >
      <Space
        direction="vertical"
        size="middle"
        style={{ width: '100%', textAlign: 'center' }}
      >
        <Paragraph type="secondary">
          An OTP code has been sent to your registered email or phone number.
        </Paragraph>

        <Input.OTP
          value={otp}
          onChange={(text) => {
            setOtp(text);
            setError(null); // Clear error when user types
          }}
          length={6}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && otp.length === 6 && !loading) {
              handleVerify();
            }
          }}
          style={{
            textAlign: 'center',
            fontSize: '18px',
            width: '100%',
            maxWidth: '250px',
            margin: '0 auto',
          }}
        />

        {error && <Text type="danger">{error}</Text>}
      </Space>
    </Modal>
  );
};

export default OTPModal;
